<script lang="ts">
	//Modules
	import AppLayout from "../layouts/AppLayout.svelte";
	import NewResultLayout from "../layouts/NewResultLayout.svelte";
	import Form from "../layouts/Form.svelte";
	import isEmail from "validator/lib/isEmail";

	//Split
	import type { TermData } from "@hitit/api";
	import {
		DefaultButton,
		DefaultSelect,
		DefaultTextInput,
		DefaultCheckbox,
		DefaultDone,
		FormInputComposite,
		InputFrame,
		OutlineButton,
	} from "@hitit/new_ui/src";

	import { t, api } from "@hitit/hub/src/stores";
	import {
		getCountries,
		getCountryCallingCode,
		parsePhoneNumber,
	} from "libphonenumber-js";
	import { navigate } from "svelte-routing";

	enum Signstep {
		basicInfomation = "basicInfomation",
		phoneAuthenticaiton = "phoneAuthenticaiton",
		term = "term",
		done = "done",
	}

	let step: Signstep = Signstep.basicInfomation;

	//step 0 inputs variables
	const countryNames: Record<string, string> = $t("global.countries", {
		returnObjects: true,
	});

	let name = "";
	let nameErrorMessage = "";
	let userName = "";
	let userNameErrorMessage = "";
	let password = "";
	let passwordErrorMessage = "";
	let passwordConfirm = "";
	let passwordConfirmErrorMessage = "";
	let emailVerificationRequesting = false;

	$: nextButtonDisabled = !name || !userName || !password || !passwordConfirm;

	$: countries = getCountries().map((v) => ({
		label: `${countryNames[v.toLowerCase()]} +${getCountryCallingCode(v)}`,
		value: getCountryCallingCode(v) as string,
		display: `+${getCountryCallingCode(v)}`,
	}));

	//step 0 functions
	async function handleBasicInfomation(e: Event) {
		if (emailVerificationRequesting) return;
		e.preventDefault();
		nameErrorMessage = !name.trim() ? $t("sso.error.required") : "";
		userNameErrorMessage = !isEmail(userName)
			? $t("sso.error.notCorrectEmailForm")
			: "";
		passwordErrorMessage = !/^[a-zA-Z\d@$!*#?&]*$/.test(password)
			? $t("sso.error.noCorrectPasswordFormat")
			: password.length < 8
			? $t("sso.error.passwordLength")
			: "";
		passwordConfirmErrorMessage =
			password !== passwordConfirm ? $t("sso.error.notMatchPassword") : "";

		if (
			nameErrorMessage ||
			userNameErrorMessage ||
			passwordErrorMessage ||
			passwordConfirmErrorMessage
		) {
			return;
		}

		try {
			emailVerificationRequesting = true;

			await $api.checkIfUsernameIsAvailable(userName);
		} catch (e) {
			const _e = e as Error;
			if (_e.message.indexOf("409") >= 0) {
				userNameErrorMessage = $t("sso.error.usedEmail");
			} else {
				userNameErrorMessage = $t("sso.error.unknownError");
			}
		} finally {
			emailVerificationRequesting = false;
		}
		if (userNameErrorMessage) return;
		step = Signstep.phoneAuthenticaiton;
	}

	//step 1 input variables
	let phoneLoading = false;
	let phone = "";
	let code = "82";
	let authenticationCode = "";
	let authenticationInputValue = "";
	let phoneErrorMeesage = "";
	let terms: TermData["termsAndConditionArticles"][] = [];
	$: phoneButtondisabled = authenticationCode
		? authenticationInputValue === ""
		: phone === "";

	//step 1 functions
	async function phoneAuthenticaitonHandler(e: Event) {
		try {
			e.preventDefault();
			if (phoneErrorMeesage) return;

			const parsedPhone = parsePhoneNumber(`+${code} ${phone}`);

			if (!(parsedPhone && parsedPhone.isValid())) {
				throw new Error($t("sso.error.phoneNumberisNotCorrectFormat"));
			}

			if (authenticationCode) {
				if (authenticationCode === authenticationInputValue) {
					step = Signstep.term;
				} else {
					throw new Error($t("sso.error.verificationNumberIsIncorrect"));
				}
			} else {
				authenticationCode = await sendAuthenticationNumb(code, phone);
			}
		} catch (e) {
			const _e = e as Error;
			phoneErrorMeesage = _e.message;
		}
	}

	function stepOneSubmitDisabledCondition(
		hasPhone: boolean,
		hasAuthValue: boolean,
		hasAuthCode: boolean
	) {
		if (phoneErrorMeesage) return true;
		if (!hasPhone) return true;
		if (hasAuthCode && !hasAuthValue) return true;

		return false;
	}
	async function resendHandler(code: string, phone: string) {
		try {
			authenticationCode = await sendAuthenticationNumb(code, phone);
		} catch (e) {
			const _e = e as Error;
			phoneErrorMeesage = _e.message;
		}
	}
	async function sendAuthenticationNumb(code: string, phone: string) {
		try {
			phoneLoading = true;
			const parsedPhone = parsePhoneNumber(`+${code} ${phone}`);
			const nationalNumber = parsedPhone?.nationalNumber;

			const { data } = await $api.requestMobileCode(nationalNumber, code);
			return data.response;
		} catch (e) {
			throw new Error($t("sso.error.unknownError"));
		} finally {
			phoneLoading = false;
		}
	}

	function phoneNumberHandler(isClear = false) {
		authenticationInputValue = "";
		authenticationCode = "";
		phoneErrorMeesage = "";
		if (isClear) phone = "";
	}

	function articleTextFormatting(text?: string) {
		return text?.replaceAll("\n", "</br>");
	}

	function authenticationNoHandler(isClear = false) {
		phoneErrorMeesage = "";
		if (isClear) authenticationInputValue = "";
	}

	function keyDownEventHandler(e: KeyboardEvent) {
		if (e.key === "-") e.preventDefault();
	}

	function transitionendAction(node: HTMLElement) {
		node.addEventListener("transitionend", () => {
			if (authenticationCode) {
				//transition end bug
				setTimeout(() => {
					node.style.overflowY = "initial";
				}, 500);
			} else {
				node.style.overflowY = "hidden";
			}
		});
	}

	//step 2 inputVariable
	let termAgree = false;
	let signupLoading = false;
	let signupError = "";

	//step 2 functions
	async function signup(e: Event) {
		try {
			e.preventDefault();
			signupLoading = true;

			const articles: { articlesId: string; agreed: boolean }[] = [];

			for (let articleCollection of terms) {
				articles.push({ articlesId: articleCollection.id, agreed: true });
			}
			const parsedPhone = parsePhoneNumber(`+${code} ${phone}`);
			const nationalNumber = parsedPhone?.nationalNumber;

			await $api.createUser({
				name,
				password,
				username: userName,
				cellphone: nationalNumber,
				cellphoneCountryCode: code,
				termsAndConditionAgreementsResponse: {
					termsAndConditionAgreementResponseList: articles,
				},
			});
			step = Signstep.done;
		} catch {
			signupError = $t("sso.error.unknownError");
		} finally {
			signupLoading = false;
		}
	}

	function TAC_Order(key: string) {
		switch (key) {
			case "힛잇 이용약관":
				return 0;
			case "전자금융거래이용약관":
				return 1;
			case "개인정보 수집 및 이용":
				return 2;
			case "개인정보 제공":
				return 3;
			default:
				return 4;
		}
	}

	async function loadTAC(count = 3) {
		try {
			const { data } = await $api.getTermsAndConditions("USER");
			const sortedTerms: TermData["termsAndConditionArticles"][] = data.sort(
				(a, b) => TAC_Order(a.name || "") - TAC_Order(b.name || "")
			);
			terms = sortedTerms;
		} catch {
			if (count === 0) {
				alert($t("sso.error.unknownError"));
				location.assign("/");
				return;
			}
			loadTAC(count - 1);
		}
	}

	//Do
	loadTAC();

</script>

<AppLayout useTerm={false}>
	{#if step === Signstep.basicInfomation}
		<Form on:submit={handleBasicInfomation}>
			<div id="signup-step-1">
				<InputFrame gap={20} direction="column">
					<FormInputComposite
						on:input={() => (nameErrorMessage = '')}
						bind:value={name}
						error={nameErrorMessage !== ''}
						errorMessage={nameErrorMessage}
						labelFor={'name'}
						inputName={$t('sso.name')}
						clearButtonCallback={() => (name = '')} />
					<FormInputComposite
						on:input={() => (userNameErrorMessage = '')}
						bind:value={userName}
						error={userNameErrorMessage !== ''}
						errorMessage={userNameErrorMessage}
						labelFor={'email'}
						inputName={$t('sso.username')}
						clearButtonCallback={() => (userName = '')} />
					<FormInputComposite
						on:input={() => (passwordErrorMessage = '')}
						bind:value={password}
						placeholder={$t('sso.signup.8_or_more')}
						error={passwordErrorMessage !== ''}
						errorMessage={passwordErrorMessage}
						password
						labelFor={'password'}
						inputName={$t('sso.password')}
						clearButtonCallback={() => (password = '')} />
					<FormInputComposite
						on:input={() => (passwordConfirmErrorMessage = '')}
						bind:value={passwordConfirm}
						placeholder={$t('sso.signup.8_or_more')}
						password
						error={passwordConfirmErrorMessage !== ''}
						errorMessage={passwordConfirmErrorMessage}
						labelFor={'passwordConfirm'}
						inputName={$t('sso.confirm_password')}
						clearButtonCallback={() => (passwordConfirm = '')} />
				</InputFrame>
				<empty />
				<DefaultButton
					disabled={nextButtonDisabled}
					isLoading={emailVerificationRequesting}>
					<span
						class="white"
						class:disabled={nextButtonDisabled}>{$t('sso.next')}</span>
				</DefaultButton>
			</div>
		</Form>
	{:else if step === Signstep.phoneAuthenticaiton}
		<Form on:submit={phoneAuthenticaitonHandler}>
			<InputFrame
				defaultClassName={'forgotIdInputFrame'}
				gap={authenticationCode !== '' ? 19.5 : 0}
				direction="column">
				<InputFrame>
					<div class="side-box">
						<DefaultSelect bind:value={code} items={countries} />
					</div>
					<div class="input-box">
						<DefaultTextInput
							id="phone"
							on:input={() => phoneNumberHandler()}
							on:keydown={(e) => keyDownEventHandler(e)}
							bind:value={phone}
							disabled={phoneLoading}
							error={phoneErrorMeesage === $t('sso.error.phoneNumberisNotCorrectFormat')}
							placeholder={$t('sso.phone')}
							clearButtonDisplay={phone.length > 0 && !phoneLoading}
							clearCallback={() => phoneNumberHandler(true)} />
					</div>
				</InputFrame>
				<div
					class="authentication-frame"
					use:transitionendAction
					class:hide={authenticationCode === ''}>
					<InputFrame>
						<div class="input-box">
							<DefaultTextInput
								id="authentication"
								on:input={() => authenticationNoHandler()}
								bind:value={authenticationInputValue}
								placeholder={$t('sso.code')}
								error={phoneErrorMeesage === $t('sso.forgot_username.code_error')}
								clearButtonDisplay={authenticationInputValue.length > 0}
								clearCallback={() => authenticationNoHandler(true)} />
						</div>
						<div class="side-box resend">
							<OutlineButton
								let:hovering={resendhover}
								on:click={() => resendHandler(code, phone)}
								disabled={phoneLoading}>
								<span class="white" class:hover={resendhover}>
									{$t('sso.resend')}
								</span>
							</OutlineButton>
						</div>
					</InputFrame>
				</div>
			</InputFrame>
			<p class="error">{phoneErrorMeesage}</p>
			<DefaultButton
				isLoading={phoneLoading}
				disabled={stepOneSubmitDisabledCondition(phone !== '', authenticationInputValue !== '', authenticationCode !== '') || phoneLoading}>
				<span class="white" class:disabled={phoneButtondisabled}>
					{authenticationCode ? $t('sso.forgot_username.confirm') : $t('sso.forgot_username.send_authentication_no')}
				</span>
			</DefaultButton>
		</Form>
	{:else if step === Signstep.term}
		<Form on:submit={signup}>
			<InputFrame gap={16} direction="column">
				<div class="term-container">
					<p class="term">
						{#each terms as term}
							{#each term.articles as article}
								{@html articleTextFormatting(article.content)}
							{/each}
						{/each}
					</p>
				</div>
				<InputFrame gap={10}>
					<div class="term-checkbox-box">
						<DefaultCheckbox id={'checkbox'} bind:checked={termAgree} />
					</div>
					<div class="term-label-box">
						<label for="checkbox">{$t('sso.signup.agree')}</label>
					</div>
				</InputFrame>
			</InputFrame>
			<p class="error">{signupError}</p>
			<DefaultButton disabled={!termAgree} isLoading={signupLoading} id={"register"}>
				<span
					class="white"
					class:disabled={!termAgree}>{$t('global.done')}</span>
			</DefaultButton>
		</Form>
	{:else if step === Signstep.done}
		<NewResultLayout text={userName}>
			<div class="done-box" slot="result-icon">
				<DefaultDone />
			</div>
			<div class="done-messages">
				<p class="send-email">{$t('sso.signup.emailSend')}</p>
				<p class="verify-email">{$t('sso.signup.verifyEmail')}</p>
			</div>
			<div class="done-button-box">
				<DefaultButton on:click={() => navigate('/')}>
					<span class="white">{$t('sso.signup.go_to_login')}</span>
				</DefaultButton>
			</div>
			<div class="email-description">
				<p>{$t('sso.signup.infomation')}</p>
				<p>{$t('sso.signup.if_not_verify')}</p>
			</div>
		</NewResultLayout>
	{/if}
</AppLayout>

<style lang="scss">
	/* step 0 */
	empty {
		display: block;
		height: 50px;
	}

	/* step 1 */
	.side-box {
		flex-basis: 100px;
		&.resend {
			flex-basis: 90px;
		}
	}
	.input-box {
		flex-grow: 1;
	}

	.authentication-frame {
		height: 40px;
		transition: height 0.5s;
		overflow-y: hidden;
		&.hide {
			height: 0px;
			overflow-y: hidden !important;
		}
	}

	span {
		transition: color 0.15s;
		&.white {
			color: white;
			&.hover {
				color: var(--signature-blue);
			}

			&.disabled {
				color: var(--default-disabled-btn-2);
			}
		}
	}

	p.error {
		box-sizing: border-box;
		padding: 10px;
		height: 50px;
		color: var(--error-color-1);
	}

	/* step2 */
	div.term-container {
		background-color: white;
		height: 400px;
		max-height: 400px;
		padding: 10px 10px 0px 10px;
		border-radius: 5px;
	}

	p.term {
		height: 100%;
		overflow: auto;
		line-height: 25px;
		box-sizing: border-box;
		//IE , Firefox not supported
		&::-webkit-scrollbar {
			width: 2px;
		}
		&::-webkit-scrollbar-track {
			background-color: transparent;
		}

		&::-webkit-scrollbar-thumb {
			background-color: #bcbfc3;
		}
	}

	.term-checkbox-box {
		width: 30px;
		height: 30px;
	}

	.term-label-box {
		display: flex;
		align-items: center;
		flex-grow: 1;
		height: 30px;

		label {
			transition: color 0.2s;
			color: white;
			font-size: 1.125rem;
			&:hover {
				cursor: pointer;
				color: rgb(235, 235, 235);
			}
		}
	}
	//step 3
	.done-box {
		width: 121px;
		height: 115.2px;
	}

	.email-description {
		color: #bcbfc3;
		margin-top: 20px;
		line-height: 22px;
		font-weight: 200;
		text-align: center;
	}
	.done-button-box {
		margin-top: 70px;
		width: 100%;
		max-width: 400px;
	}

	.done-messages {
		margin-top: 20px;
		color: #ffffff;
		font-size: 1.125rem;
		line-height: 25px;
		text-align: center;

		.verify-email {
			font-weight: 500;
		}
	}

</style>
